"use client";

import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";

import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import CoverageAreaForm from "@/components/templates/CoverageAreaForm";
import {
  buildCoverageAreaFormSchema,
  buildFormInitialData,
  normalizeCoverageAreaFormData,
} from "@/components/templates/CoverageAreaForm/form-data";
import { CoverageAreaFormData } from "@/components/templates/CoverageAreaForm/types";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useCreateCoverageArea from "@/hooks/requests/coverage-area/use-create-coverage-area";
import useGetCoverageAreaById from "@/hooks/requests/coverage-area/use-get-coveragea-rea-by-id";
import useUpdateCoverageArea from "@/hooks/requests/coverage-area/use-update-coverage-area";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import useMutate from "@/hooks/use-mutate";
import { CoverageAreaMessages } from "@/messages/coverage-area/coverage-area.messages";
import { BreadCrumb } from "@/types/page.types";

type Props = {
  messages: CoverageAreaMessages;
};

function CoverageAreaFormPage({ messages }: Props) {
  const { coverageAreaId } = useParams();
  const customerId = useGetCustomerId();

  const isEditing = !!coverageAreaId;
  const _id = isEditing ? Number(coverageAreaId) : 0;
  const { data, isLoading: isLoadingCoverageArea, error } = useGetCoverageAreaById({ _id });
  const { create, isLoading: isLoadingCreate } = useCreateCoverageArea();
  const { update, isLoading: isLoadingUpdate } = useUpdateCoverageArea();
  const { mutate } = useMutate();
  const router = useNavigate();
  const { lang } = useLanguage();

  const onSuccess = () => {
    mutate(SWR_KEYS.getCoverageAreaById);
    mutate(SWR_KEYS.getCoverageAreas);
    router(ROUTE_URLS.coverageArea(lang));
  };

  const handleUpdate = async (_formData: CoverageAreaFormData) => {
    if (!data) return;

    const _id = Number(data.id);
    const formData = normalizeCoverageAreaFormData(_formData);
    const successMessage = messages.updateCoverageAreaSuccess;
    const errorMessage = messages.updateCoverageAreaError;

    await update({ _id, formData, successMessage, errorMessage, onSuccess });
  };

  const handleCreate = async (_formData: CoverageAreaFormData) => {
    const formData = normalizeCoverageAreaFormData(_formData);
    const successMessage = messages.createCoverageAreaSuccess;
    const errorMessage = messages.createCoverageAreaError;

    await create({ formData, successMessage, errorMessage, onSuccess });
  };

  const handleSubmitForm = async (_formData: CoverageAreaFormData) => {
    if (isEditing) await handleUpdate(_formData);
    else await handleCreate(_formData);
  };

  const breadCrumb = { title: messages.breadCrumbTitle, href: ROUTE_URLS.coverageArea(lang) };

  const BREAD_CRUMBS: BreadCrumb[] = [breadCrumb];

  const isSuspended = isEditing ? isLoadingCoverageArea : false;
  const isLoading = isLoadingCreate || isLoadingUpdate || isLoadingCoverageArea;
  const pageTitle = isEditing ? messages.updatePageTitle : messages.createPageTitle;

  const shouldLoadCoverageAreaForm = isEditing ? !!data && !error : true;
  const shouldLoadError = !data && !!error && !isLoading;

  if (!customerId) return <CircularProgress />;

  return (
    <PageLayout messages={messages} title={pageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Suspense fallback={<CircularProgress />} isSuspended={isSuspended || !customerId}>
          {shouldLoadCoverageAreaForm && (
            <Formik
              initialValues={buildFormInitialData(customerId, data)}
              validationSchema={buildCoverageAreaFormSchema(messages)}
              validateOnChange={false}
              onSubmit={handleSubmitForm}
            >
              <CoverageAreaForm messages={messages} isLoading={isLoading} isEditing={isEditing} />
            </Formik>
          )}

          {!!shouldLoadError && (
            <Card sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <AlertBlock
                title={messages.coverageAreaErrorToLoad}
                description={error.message}
                alt={messages.coverageAreaNotFoundAlt}
              />
            </Card>
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}

export default CoverageAreaFormPage;
